/**
 * Custom Form Template
 */

import { createBlockWrapper } from '../utils';
import { applyThemeOverrides } from './utils';
import type { FormField, TemplateRenderer } from './types';

export const renderCustomForm: TemplateRenderer = (
  config,
  _data,
  container,
  onAction,
  themeOverrides
) => {
  const title = config.title as string | undefined;
  const fields = (config.fields as FormField[]) || [];
  const submitLabel = (config.submitLabel as string) || 'Submit';
  const submitAction = (config.submitAction as string) || 'flow_submit';

  const wrapper = createBlockWrapper('custom-form');
  applyThemeOverrides(wrapper, themeOverrides);

  if (title) {
    const titleEl = document.createElement('div');
    titleEl.className = 'pms-form-title';
    titleEl.textContent = title;
    wrapper.appendChild(titleEl);
  }

  const form = document.createElement('form');
  form.className = 'pms-custom-form';
  form.addEventListener('submit', (e) => {
    e.preventDefault();

    // Collect form data
    const formData: Record<string, string> = {};
    for (const field of fields) {
      const input = form.querySelector(`[name="${field.key}"]`) as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (input) {
        formData[field.key] = input.value;
      }
    }

    // Submit via action
    if (onAction) {
      onAction(`${submitAction}:${JSON.stringify(formData)}`);
    }
  });

  // Create form fields
  for (const field of fields) {
    const fieldEl = document.createElement('div');
    fieldEl.className = 'pms-form-field';

    const label = document.createElement('label');
    label.className = 'pms-form-label';
    label.textContent = field.label + (field.required ? ' *' : '');
    fieldEl.appendChild(label);

    let inputEl: HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;

    if (field.type === 'select' && field.options) {
      inputEl = document.createElement('select');
      const placeholder = document.createElement('option');
      placeholder.value = '';
      placeholder.textContent = field.placeholder || `Select ${field.label}`;
      placeholder.disabled = true;
      placeholder.selected = true;
      inputEl.appendChild(placeholder);
      for (const opt of field.options) {
        const option = document.createElement('option');
        option.value = opt;
        option.textContent = opt;
        inputEl.appendChild(option);
      }
    } else if (field.type === 'textarea') {
      inputEl = document.createElement('textarea');
      inputEl.rows = 4;
      if (field.placeholder) inputEl.placeholder = field.placeholder;
    } else {
      inputEl = document.createElement('input');
      inputEl.type = field.type || 'text';
      if (field.placeholder) inputEl.placeholder = field.placeholder;
    }

    inputEl.name = field.key;
    inputEl.className = 'pms-form-input';
    if (field.required) inputEl.required = true;

    fieldEl.appendChild(inputEl);
    form.appendChild(fieldEl);
  }

  // Submit button
  const submitEl = document.createElement('button');
  submitEl.type = 'submit';
  submitEl.className = 'pms-form-submit';
  submitEl.textContent = submitLabel;
  form.appendChild(submitEl);

  wrapper.appendChild(form);
  container.appendChild(wrapper);
};
